import type { UserManagerSettings } from 'oidc-client-ts';
import type { JSX, ReactNode } from 'react';
import { AuthProvider } from 'react-oidc-context';

export interface PortalAuthProviderProps extends UserManagerSettings {
  children: ReactNode;
}

/**
 * PortalAuthProvider wraps react-oidc-context's AuthProvider with
 * sensible defaults for the NRx provider/staff portals.
 *
 * - automaticSilentRenew: true — renews tokens ~1 min before expiry via hidden iframe
 * - loadUserInfo: true — fetches userinfo claims from the OIDC userinfo endpoint
 * - monitorSession: true — monitors session for external logout/changes
 * - No userStore override — tokens stored in memory only (HIPAA compliance)
 *   The OIDC state nonce is stored in sessionStorage by oidc-client-ts (not a token).
 */
export function PortalAuthProvider({
  children,
  ...userManagerSettings
}: PortalAuthProviderProps): JSX.Element {
  const settings: UserManagerSettings = {
    automaticSilentRenew: true,
    loadUserInfo: true,
    monitorSession: true,
    validateSubOnSilentRenew: true,
    // stateStore uses sessionStorage by default in oidc-client-ts (for PKCE state nonce)
    // No userStore override — access/refresh tokens stored in memory only
    ...userManagerSettings,
  };

  return <AuthProvider {...settings}>{children}</AuthProvider>;
}
